import { GamePackIcon, GamePackPalette } from '@/API';
import { createGamePack } from '@/client/gamePacks';
import { listOrganizations } from '@/client/organization';
import { Organization } from '@/models/Organization.model';
import { gamePackIconOptions, gamePackPaletteOptions } from '@/utils/gamePacks';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

interface CreateGamePackDialogProps {
  gamePackId?: string;

  isOpen: boolean;

  setIsOpen: (isOpen: boolean) => void;
  onSubmit?: () => void;
}

type SelectedOrganization = Partial<Organization> & {
  selected: boolean;
};

type GamePackInputs = {
  name: string;
  description: string;
  category1: string;
  category2: string;
  category3: string;
  icon: GamePackIcon;
  palette: GamePackPalette;
  status: string;
  showInDiscover: boolean;
  isNew: boolean;
  orgAccessAll: string;
  organizations: SelectedOrganization[];
};

export const CreateGamePackDialog = ({
  setIsOpen,
  onSubmit,
  isOpen
}: CreateGamePackDialogProps) => {
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setFocus,
    watch
  } = useForm<GamePackInputs>({
    mode: 'onSubmit',
    defaultValues: {
      orgAccessAll: 'false',
      status: 'true',
      showInDiscover: false,
      isNew: true,
      category1: '',
      category2: '',
      category3: '',
      organizations: []
    }
  });
  const { fields, replace: replaceOrgs } = useFieldArray({
    control,
    name: 'organizations' // unique name for your Field Array,
  });
  const orgAccessAllValue = watch('orgAccessAll');
  const watchedOrgs = (watch('organizations') as any[]) || [];
  const hasAtLeastOneSelected = watchedOrgs.some((o: any) => o?.selected === true);
  const isSaveDisabled =
    submitDisabled || (orgAccessAllValue === 'false' && !hasAtLeastOneSelected);

  useEffect(() => {
    if (isOpen === true) {
      reset();
      listOrganizations().then((result) => {
        const _orgs = result
          .map((item: any) => {
            if (item) {
              return {
                id: item.id as string,
                name: item.name as string,
                selected: false,
                tenantId: item.tenant as string
              } as SelectedOrganization;
            }
            return undefined;
          })
          .filter((item: any) => item !== undefined) as SelectedOrganization[];
        replaceOrgs(_orgs ?? []);
        setFocus('name');
      });
    }
  }, [isOpen]);

  const onCreate = async (data: GamePackInputs) => {
    setSubmitDisabled(true);
    const selectedOrgs = data.organizations.filter((org) => org.selected === true);
    const organizationIds = selectedOrgs.map((org) => org.id).filter(Boolean) as string[];

    const sanitizeCategory = (value?: string) => {
      const trimmed = value?.trim();
      if (!trimmed) {
        return undefined;
      }
      return trimmed.slice(0, 12);
    };

    try {
      // Create the GamePack with organization associations
      await createGamePack({
        name: data.name,
        description: data.description,
        category1: sanitizeCategory(data.category1),
        category2: sanitizeCategory(data.category2),
        category3: sanitizeCategory(data.category3),
        icon: data.icon,
        palette: data.palette,
        status: data.status === 'true' ? 'ACTIVE' : 'DISABLED',
        showInDiscover: data.showInDiscover,
        isNew: data.isNew,
        organizationIds: organizationIds
      });

      setIsOpen(false);
      reset();
      if (onSubmit) {
        onSubmit();
      }
    } catch (err) {
      console.error({ err });
      throw new Error('Could not create game pack');
    } finally {
      setSubmitDisabled(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth={'md'} fullWidth={true}>
      <DialogTitle className="text-[24px] font-bold">Create Game Pack</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onCreate)}>
          {/* Title */}
          <DialogContentText className="text-black">Game Pack Title</DialogContentText>
          <TextField
            className="mb-[36px]"
            margin="dense"
            id="0"
            type="text"
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="Give this Game Pack a unique name"
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', {
              required: 'Game pack name is required'
            })}
          />
          {/* Description */}
          <DialogContentText className="text-black">Description</DialogContentText>
          <TextField
            className="mb-[36px]"
            margin="dense"
            id="0"
            type="text"
            fullWidth
            variant="outlined"
            placeholder="Give this Game Pack a unique name"
            error={!!errors.description}
            helperText={errors.description?.message ?? `${watch('description')?.length ?? 0}/100`}
            {...register('description', {
              required: 'Game pack description is required',
              maxLength: 100
            })}
          />
          {/* Categories */}
          <DialogContentText className="text-black">Categories (optional)</DialogContentText>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3 mb-36">
            <TextField
              margin="dense"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Category 1"
              inputProps={{ maxLength: 12 }}
              error={!!errors.category1}
              helperText={errors.category1?.message ?? `${watch('category1')?.length ?? 0}/12`}
              {...register('category1', {
                maxLength: { value: 12, message: '12 characters max' }
              })}
            />
            <TextField
              margin="dense"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Category 2"
              inputProps={{ maxLength: 12 }}
              error={!!errors.category2}
              helperText={errors.category2?.message ?? `${watch('category2')?.length ?? 0}/12`}
              {...register('category2', {
                maxLength: { value: 12, message: '12 characters max' }
              })}
            />
            <TextField
              margin="dense"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Category 3"
              inputProps={{ maxLength: 12 }}
              error={!!errors.category3}
              helperText={errors.category3?.message ?? `${watch('category3')?.length ?? 0}/12`}
              {...register('category3', {
                maxLength: { value: 12, message: '12 characters max' }
              })}
            />
          </div>
          <DialogContentText className="text-black">Icon</DialogContentText>
          <Select
            fullWidth
            id="icon-select"
            margin="dense"
            {...register('icon')}
            className="mb-[36px]"
            variant="outlined"
            defaultValue={gamePackIconOptions[0].value}
          >
            {gamePackIconOptions.map((iconOption) => (
              <MenuItem value={iconOption.value} key={iconOption.value}>
                {iconOption.label}
              </MenuItem>
            ))}
          </Select>
          <DialogContentText className="text-black">Palette</DialogContentText>
          <Select
            fullWidth
            id="palette-select"
            margin="dense"
            {...register('palette')}
            className="mb-[36px]"
            variant="outlined"
            defaultValue={gamePackPaletteOptions[0].value}
          >
            {gamePackPaletteOptions.map((paletteOption) => (
              <MenuItem value={paletteOption.value} key={paletteOption.value}>
                {paletteOption.label}
              </MenuItem>
            ))}
          </Select>
          {/* Status */}
          <div>
            <FormControl className="mb-32 mr-64">
              <FormLabel id="status-radio-buttons-group-label" className="text-black">
                Game Pack Status
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="status-radio-buttons-group-label"
                defaultValue="true"
              >
                <FormControlLabel
                  value="true"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Active"
                  {...register('status')}
                />
                <FormControlLabel
                  value="false"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Disabled"
                  {...register('status')}
                />
              </RadioGroup>
            </FormControl>
            {/* Homepage Visibility */}
            <FormControl className="mb-32 mr-64">
              <FormLabel className="text-black">Homepage Visibility</FormLabel>
              <Controller
                name="showInDiscover"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        onChange={(_event, checked) => field.onChange(checked)}
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="Show in the Discover section"
                  />
                )}
              />
            </FormControl>
            {/* New GamePack */}
            <FormControl className="mb-32 mr-64">
              <FormLabel className="text-black">New Game Pack</FormLabel>
              <Controller
                name="isNew"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        onChange={(_event, checked) => field.onChange(checked)}
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="Is New"
                  />
                )}
              />
            </FormControl>
          </div>
          <div>
            {/* Org Access */}
            <FormControl className="mb-10">
              <FormLabel id="demo-row-radio-buttons-group-label" className="text-black">
                Organization Access
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                defaultValue={'true'}
              >
                <FormControlLabel
                  value={'true'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="All"
                  {...register('orgAccessAll')}
                />
                <FormControlLabel
                  value={'false'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Selected Organizations Only"
                  {...register('orgAccessAll')}
                />
              </RadioGroup>
            </FormControl>
            {orgAccessAllValue === 'false' ? (
              <>
                <Stack>
                  {fields.map((field, index) => {
                    return (
                      <FormControlLabel
                        key={`org-checkbox-${field.id}`}
                        control={
                          <Checkbox
                            defaultChecked={false}
                            {...register(`organizations.${index}.selected`)}
                          />
                        }
                        label={field.name}
                      />
                    );
                  })}
                </Stack>
              </>
            ) : (
              <></>
            )}
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <button
          className="btn btn-neutral secondary mr-[5px]"
          onClick={() => {
            setIsOpen(false);
          }}
        >
          Cancel
        </button>
        <button
          disabled={isSaveDisabled}
          className="btn btn-primary"
          onClick={handleSubmit(onCreate)}
        >
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
};
